/**
 * Owner machine registration — construction heavy equipment only.
 * Two mobility classes: wheeled (RTO) vs crawler (site, no RTO road passing).
 * Trucks / commercial transport are not part of this platform.
 */

export const MOBILITY_WHEELED = 'wheeled';
export const MOBILITY_TRACKED = 'tracked';

export const MOBILITY_OPTIONS = [
  {
    id: MOBILITY_WHEELED,
    label: 'Wheeled',
    labelMr: 'व्हील्ड (चाकांवर)',
    hint: 'JCB 3DX, wheeled loader, grader — construction plant with RTO (RC)',
    hintMr: 'JCB 3DX, wheeled loader, grader — बांधकाम उपकरण, RTO (RC) लागते',
    icon: '🛞',
  },
  {
    id: MOBILITY_TRACKED,
    label: 'Crawler',
    labelMr: 'क्रॉलर (चेन)',
    hint: 'Poclain / chain excavator, dozer, crawler crane — site plant, no RTO passing',
    hintMr: 'Poclain (chain excavator), dozer — साइटवर, RTO passing नाही',
    icon: '🔗',
  },
];

/** Construction heavy equipment categories — no trucks or transport vehicles. */
export const MACHINE_TYPES_BY_MOBILITY = {
  [MOBILITY_WHEELED]: [
    'Backhoe Loader (JCB 3DX type)',
    'Wheeled Loader',
    'Skid Steer Loader',
    'Motor Grader',
    'Soil Compactor / Roller (Wheeled)',
    'Telescopic Handler (Telehandler)',
    'Mobile Crane (Wheeled / Pick-and-Carry)',
    'Other Wheeled Construction Equipment',
  ],
  [MOBILITY_TRACKED]: [
    'Excavator / Poclain',
    'Crawler Crane',
    'Bulldozer / Dozer',
    'Crawler Loader',
    'Other Crawler Construction Equipment',
  ],
};

const DOC_WHEELED = [
  { key: 'rcBook', labelKey: 'docRcBook', required: true },
  { key: 'insurance', labelKey: 'docInsurance', required: true },
  { key: 'pollution', labelKey: 'docPollution', required: true },
  { key: 'identity', labelKey: 'docIdentity', required: true },
];

const DOC_TRACKED = [
  { key: 'chassisPlate', labelKey: 'docChassisPlate', required: true },
  { key: 'machineInvoice', labelKey: 'docMachineInvoice', required: true },
  { key: 'equipmentPhoto', labelKey: 'docEquipmentPhoto', required: true },
  { key: 'insurance', labelKey: 'docInsuranceOptional', required: false },
  { key: 'identity', labelKey: 'docIdentity', required: true },
];

export const isRtoApplicable = (mobilityClass) => mobilityClass === MOBILITY_WHEELED;

export const getDocFieldsForMobility = (mobilityClass) => {
  if (mobilityClass === MOBILITY_TRACKED) return DOC_TRACKED;
  if (mobilityClass === MOBILITY_WHEELED) return DOC_WHEELED;
  return [];
};

export const emptyRegDocumentsForMobility = (mobilityClass) => {
  const docs = getDocFieldsForMobility(mobilityClass);
  return docs.reduce((acc, doc) => ({ ...acc, [doc.key]: null }), {});
};

/** All upload keys the API accepts (multer field names). */
export const ALL_REGISTRATION_DOC_KEYS = [
  'rcBook',
  'insurance',
  'pollution',
  'identity',
  'chassisPlate',
  'machineInvoice',
  'equipmentPhoto',
];

export const getPdiChecksForMobility = (mobilityClass) => {
  if (!mobilityClass) return [];
  const wheeled = isRtoApplicable(mobilityClass);
  return [
    { key: 'pdiChecked', labelKey: 'pdiCompleted', required: true },
    {
      key: 'insuranceValid',
      labelKey: wheeled ? 'pdiInsuranceValid' : 'pdiInsuranceApplicable',
      required: wheeled,
    },
    {
      key: 'pollutionValid',
      labelKey: 'pdiPollutionValid',
      required: wheeled,
    },
  ];
};

export const validateRegistrationStep = (step, data, documents, t) => {
  const mobility = data.mobilityClass;
  const phoneDigits = String(data.ownerPhone || '').replace(/\D/g, '');

  if (step === 1) {
    if (!data.ownerName?.trim()) return t('valOwnerName');
    if (phoneDigits.length < 10) return t('valPhone');
    if (!data.ownerEmail?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.ownerEmail.trim())) {
      return t('valEmail');
    }
  }

  if (step === 2) {
    if (!mobility) return t('valMobility');
    if (!data.machineName?.trim()) return t('valMachineName');
    if (!data.machineType?.trim()) return t('valMachineType');
    if (isRtoApplicable(mobility)) {
      if (!data.regNo?.trim()) return t('valRto');
    }
    if (!data.year?.trim() || !/^\d{4}$/.test(data.year.trim())) return t('valYear');
    if (!data.rate_per_hour || Number(data.rate_per_hour) <= 0) {
      return t('valHourlyRate') || 'Hourly rate is required';
    }
    if (data.is_negotiable) {
      const floor = Number(data.minimum_rate_per_cycle);
      if (!Number.isFinite(floor) || floor <= 0) {
        return t('valNegotiableFloor')
          || 'Minimum rate per cycle is required when negotiable rates are enabled';
      }
    }
    const dueDays = Number(data.payment_due_days);
    if (data.payment_due_days != null && (!Number.isFinite(dueDays) || dueDays < 7 || dueDays > 45)) {
      return 'Payment due days must be between 7 and 45';
    }
    const earlyDays = Number(data.early_payment_days);
    if (data.early_payment_days != null && (!Number.isFinite(earlyDays) || earlyDays < 1 || earlyDays > 7)) {
      return 'Early payment window must be between 1 and 7 days';
    }
    const earlyPct = Number(data.early_payment_discount_pct);
    if (data.early_payment_discount_pct != null && (!Number.isFinite(earlyPct) || earlyPct < 0 || earlyPct > 5)) {
      return 'Early payment discount must be between 0 and 5%';
    }
    const latePct = Number(data.late_payment_fee_pct);
    if (data.late_payment_fee_pct != null && (!Number.isFinite(latePct) || latePct < 0 || latePct > 2)) {
      return 'Late payment fee must be between 0 and 2%';
    }
  }

  if (step === 3) {
    const checks = getPdiChecksForMobility(mobility);
    for (const item of checks) {
      if (item.required && !data[item.key]) {
        return t('valPdiChecks');
      }
    }
    if (!data.pdiChecked) return t('valPdiRequired');
  }

  if (step === 4) {
    const docFields = getDocFieldsForMobility(mobility);
    const missing = docFields.filter((doc) => doc.required && !documents[doc.key]);
    if (missing.length > 0) {
      const docs = missing.map((d) => t(d.labelKey)).join(', ');
      return (t('valUploadMissing') || 'Please upload: {docs}').replace('{docs}', docs);
    }
  }

  if (step === 5) {
    if (!data.agreed) return t('acceptTermsRequired');
  }

  return '';
};

export const ATTACHMENT_OPTIONS = [
  { id: 'standard_bucket', label: 'Standard digging / loading bucket' },
  { id: 'loading_bucket', label: 'High-capacity loading bucket' },
  { id: 'pipeline_bucket', label: 'Pipeline / trenching bucket' },
  { id: 'rock_breaker', label: 'Rock / concrete breaker' },
  { id: 'grading_blade', label: 'Levelling / grading blade' },
  { id: 'ripper', label: 'Ripper' },
  { id: 'other', label: 'Other' },
];

export const ATTACHMENT_LABEL_BY_ID = Object.fromEntries(
  ATTACHMENT_OPTIONS.map((opt) => [opt.id, opt.label]),
);

export const RATE_FIELDS = [
  {
    key: 'rate_per_day',
    label: 'Daily Rate',
    suffix: '/ day',
    required: false,
    hint: 'Optional — for full-day bookings',
  },
  {
    key: 'rate_per_week',
    label: 'Weekly Rate',
    suffix: '/ week',
    required: false,
    hint: 'Optional',
  },
  {
    key: 'rate_per_month',
    label: 'Monthly Rate',
    suffix: '/ month',
    required: false,
    hint: 'Optional',
  },
];

/** Optional contract/custom quote terms — Phase 5B prerequisite for 5A-i floor check. */
export const NEGOTIABLE_RATE_FIELDS = {
  isNegotiableKey: 'is_negotiable',
  floorKey: 'minimum_rate_per_cycle',
};

export const ATTACHMENT_RATE_ELIGIBLE = [
  'rock_breaker',
  'pipeline_bucket',
  'loading_bucket',
  'grading_blade',
  'ripper',
];
